import { BehaviorSubject, Observable } from 'rxjs';
import { SettingsService } from './settings/settings.service';
import { Injectable } from '@angular/core';
import { DocumentManagerService } from './document/document-manager.service';
import { AsgardDocPdf } from '../core/documents/asgard-doc-pdf';
import { ReadingMode, ReadingBase } from '../core/reading-utils/enums';
import { AsgardDocEpub } from '../core/documents/asgard-doc-epub';

@Injectable({
  providedIn: 'root'
})
export class ReadingModesService {
  private currentReadingModeSubj: BehaviorSubject<ReadingMode> = new BehaviorSubject<ReadingMode>(ReadingMode.ClickToPlay);
  private readonly readingModeStorage: Map<string, { mode: ReadingMode, base: ReadingBase }> =
    new Map<string, { mode: ReadingMode, base: ReadingBase }>();

  // TODO: specify proper type here
  private readingModes = [{
    mode: ReadingMode.ClickToPlay,
    iconClass: 'fas fa-mouse-pointer',
    pdfReader: true,
    editor: true,
    epubReader: true,
    tooltip: 'TOOLBARS.CLICK_TO_PLAY'
  }, {
    mode: ReadingMode.ClickToRead,
    iconClass: 'fas fa-bullseye-pointer',
    pdfReader: true,
    editor: true,
    epubReader: true,
    tooltip: 'TOOLBARS.CLICK_TO_READ'
  }, {
    mode: ReadingMode.Hover,
    iconClass: 'far fa-hand-pointer',
    pdfReader: true,
    editor: true,
    epubReader: false,
    tooltip: 'TOOLBARS.HOVER_TO_READ'
  }, {
    mode: ReadingMode.Selection,
    iconClass: 'fas fa-font',
    pdfReader: true,
    editor: true,
    epubReader: false,
    tooltip: 'TOOLBARS.READ_SELECTION'
  }, {
    mode: ReadingMode.RectangleSelection,
    iconClass: 'far fa-object-group',
    pdfReader: true,
    editor: false,
    epubReader: false,
    tooltip: 'TOOLBARS.READ_RECTANGLE'
  }];

  get readingMode(): ReadingMode {
    const readingMode = this.docMgrService.currentDoc && this.readingModeStorage.get(this.docMgrService.currentDoc.documentId);
    return readingMode ? readingMode.mode : this.currentReadingModeSubj.getValue();
  }

  get readingBase(): ReadingBase {
    const readingMode = this.docMgrService.currentDoc && this.readingModeStorage.get(this.docMgrService.currentDoc.documentId);
    return readingMode ? readingMode.base : this.settingsService.settings.readingBase;
  }

  get globalReadingModeObservable(): Observable<ReadingMode> {
    return this.currentReadingModeSubj.asObservable();
  }

  constructor(
    private readonly settingsService: SettingsService,
    private readonly docMgrService: DocumentManagerService,
  ) {
    this.initSubscriptions();
  }

  // TODO: specify proper type here
  getReadingModesForCurrentDoc() {
    if (this.isCurrentDocPdf()) {
      return this.readingModes.filter(x => x.pdfReader);
    } else if (this.isCurrentDocEpub()) {
      return this.readingModes.filter(x => x.epubReader);
    } else {
      return this.readingModes.filter(x => x.editor);
    }
  }

  setGlobalReadingMode(mode: ReadingMode): void {
    this.currentReadingModeSubj.next(mode);
    if (this.isCurrentDocPdf()) {
      this.settingsService.saveSettings({ readingMode: mode });
    } else if (this.isCurrentDocEpub()) {
      this.settingsService.saveSettings({ epubReadingMode: mode });
    } else {
      this.settingsService.saveSettings({ editorReadingMode: mode });
    }
  }

  setReadingModeForCurrentDoc(mode: ReadingMode, base: ReadingBase): void {
    if (this.docMgrService.currentDoc) {
      this.readingModeStorage.set(this.docMgrService.currentDoc.documentId, { mode, base });
    }
  }

  private isCurrentDocPdf(): boolean {
    return this.docMgrService.currentDoc !== null && this.docMgrService.currentDoc instanceof AsgardDocPdf;
  }

  private isCurrentDocEpub(): boolean {
    return this.docMgrService.currentDoc !== null && this.docMgrService.currentDoc instanceof AsgardDocEpub;
  }

  private initSubscriptions(): void {
    this.docMgrService.currentDocumentChanged
      .subscribe((docId: string | null) => {

        this.updateCurrentReadingMode(docId);
      });
    this.docMgrService.documentReady
      .subscribe((docId: string | null) => {
        this.updateCurrentReadingMode(docId);
      });
    this.docMgrService.documentRemoved
      .subscribe(docId => this.deleteReadingModeForDocument(docId));
  }

  private updateCurrentReadingMode(docId: string | null): void {
    if (this.docMgrService.currentDoc && !this.docMgrService.currentDoc.isDocumentReady) {
      return;
    }

    const doc = this.docMgrService.getDocument(docId);

    if (doc) {
      if (doc instanceof AsgardDocPdf && this.settingsService.settings.readingMode) {
        this.currentReadingModeSubj.next(this.settingsService.settings.readingMode);
      } else if (doc instanceof AsgardDocEpub && this.settingsService.settings.epubReadingMode) {
        this.currentReadingModeSubj.next(this.settingsService.settings.epubReadingMode);
      } else {
        this.currentReadingModeSubj.next(this.settingsService.settings.editorReadingMode);
      }
    }
  }

  private deleteReadingModeForDocument(docId: string | null): void {
    if (docId && this.readingModeStorage.has(docId)) {
      this.readingModeStorage.delete(docId);
    }
  }
}
